import { useMediaQuery, useTheme } from '@mui/material'
import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridApi,
} from '@mui/x-data-grid'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { Scout } from '../../types.ts'

import { getColumns } from './config.tsx'
import { COLUMN_VISIBILITY_STORAGE_KEY } from './constants.ts'
import { tableCustomStyle } from './style.ts'

interface ScoutTableProps {
  scouts: Scout[]
  loading?: boolean
  page: number
  pageSize: number
  totalCount?: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onRowClick?: (scoutId: string) => void
  onFilterModelChange?: (model: GridFilterModel) => void
  filterModel?: GridFilterModel
  canDelete?: boolean
  apiRef?: React.MutableRefObject<GridApi | null>
}

export const ScoutTable: React.FC<ScoutTableProps> = ({
  scouts,
  loading = false,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  onFilterModelChange,
  filterModel,
  canDelete = true,
  apiRef,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const columns: GridColDef<Scout>[] = useMemo(
    () => getColumns({ isMobile, canDelete }),
    [isMobile, canDelete]
  )

  // Initialize column visibility from localStorage
  const getInitialColumnVisibility = (): GridColumnVisibilityModel => {
    try {
      const stored = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored) as GridColumnVisibilityModel
      }
    } catch (error) {
      console.error('Error loading column visibility:', error)
    }
    return {}
  }

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    getInitialColumnVisibility
  )

  // Save column visibility to localStorage when it changes
  const handleColumnVisibilityChange = useCallback(
    (newModel: GridColumnVisibilityModel) => {
      // Check if at least one column (excluding actions) will be visible
      const visibleColumns = columns.filter(col => newModel[col.field] !== false)

      // If no columns would be visible, keep at least the email column visible
      if (visibleColumns.length === 0) {
        newModel = { ...newModel, email: true }
      }

      setColumnVisibilityModel(newModel)
      try {
        localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(newModel))
      } catch (error) {
        console.error('Error saving column visibility:', error)
      }
    },
    [columns]
  )

  // Apply responsive defaults to column visibility if not already set by user
  useEffect(() => {
    const storedVisibility = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY)
    if (!storedVisibility) {
      // Only apply responsive defaults if user hasn't customized visibility
      const defaultVisibility: GridColumnVisibilityModel = {
        email: true, // Email is always visible by default
      }

      if (isMobile) {
        defaultVisibility.phoneNumber = false
        defaultVisibility.emailVerification = false
        defaultVisibility.address = false
      }

      setColumnVisibilityModel(defaultVisibility)
    }
  }, [isMobile, isTablet])

  return (
    <DataGrid
      apiRef={apiRef}
      rows={scouts}
      columns={columns}
      loading={loading}
      pageSizeOptions={isMobile ? [5, 10, 25] : [10, 25, 50]}
      density={isMobile ? 'compact' : 'comfortable'}
      paginationMode="server"
      rowCount={totalCount}
      getRowId={row => row.uuid}
      paginationModel={{
        page: page,
        pageSize,
      }}
      onPaginationModelChange={model => {
        if (model.page !== page) {
          onPageChange(model.page)
        }
        if (model.pageSize !== pageSize) {
          onPageSizeChange(model.pageSize)
        }
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityChange}
      onRowClick={params => onRowClick?.(String(params.row.uuid))}
      disableRowSelectionOnClick
      filterMode="server"
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      sx={tableCustomStyle}
    />
  )
}
